import { useEffect, useState } from 'react'
import { Link, useParams, Navigate, useLocation } from 'react-router-dom'
import ProjectCaseStudySections from '../components/ProjectCaseStudySections'
import ProjectMedia from '../components/ProjectMedia'
import CaseStudyMedia from '../components/CaseStudyMedia'
import CaseStudyMediaCarousel from '../components/CaseStudyMediaCarousel'
import ToggleSwitch from '../components/ToggleSwitch'
import {
  getAdjacentProjects,
  getProjectById,
  getProjectDetailsPath,
} from '../lib/utils/projects'
import { enrichCaseStudyCarouselItems } from '../lib/socialCarouselCards'
import { PROJECT_TOPIC_LABEL_CLASS } from '../lib/projectTopicLabel'
import { usePageTitle } from '../lib/hooks/usePageTitle'

const renderDetailSectionMedia = (media, mediaLayout, sectionTitle) => {
  if (!media) return null

  const items = Array.isArray(media) ? media : [media]
  if (!items.length) return null

  if (mediaLayout === 'carousel') {
    return (
      <div className="pt-3">
        <CaseStudyMediaCarousel items={items} />
      </div>
    )
  }

  if (items.length === 1) {
    return (
      <div className="pt-3">
        <CaseStudyMedia
          item={items[0]}
          title={items[0].label || sectionTitle}
        />
      </div>
    )
  }

  return (
    <div className="grid gap-6 pt-3 sm:grid-cols-2">
      {items.map((item) => (
        <CaseStudyMedia key={item.id || item.label} item={item} />
      ))}
    </div>
  )
}

const ProjectDetail = () => {
  const { projectId } = useParams()
  const location = useLocation()
  const project = getProjectById(projectId)
  const [isAutoPlay, setIsAutoPlay] = useState(true)
  const [enterWash, setEnterWash] = useState(() => {
    if (location.state?.fromExplore) return null
    const color = location.state?.enterColor
    return color ? { color, opacity: 1 } : null
  })
  const hasSections = Boolean(project?.sections?.length)
  const fromExplore = Boolean(location.state?.fromExplore)
  usePageTitle(project?.title)

  useEffect(() => {
    setIsAutoPlay(true)
  }, [projectId])

  useEffect(() => {
    if (!enterWash) return

    const fadeFrame = window.requestAnimationFrame(() => {
      setEnterWash((prev) => (prev ? { ...prev, opacity: 0 } : null))
    })
    const clearTimer = window.setTimeout(() => setEnterWash(null), 700)
    return () => {
      window.cancelAnimationFrame(fadeFrame)
      window.clearTimeout(clearTimer)
    }
    // Only run the exit fade once per landing (enter wash bootstrap)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId])

  if (!project) {
    return <Navigate to="/" replace />
  }

  const { previous, next } = getAdjacentProjects(project.id)
  const previewVideo = project.heroVideo || project.previewVideo
  const projectVideo = hasSections ? null : previewVideo

  const handleAutoPlayChange = (nextValue) => {
    setIsAutoPlay(nextValue)
  }

  return (
    <section className="content-page project-detail relative px-5 py-4 md:px-8 md:py-5">
      {enterWash ? (
        <div
          className="pointer-events-none fixed inset-0 z-[var(--z-dock)] transition-opacity duration-700 ease-out"
          style={{ backgroundColor: enterWash.color, opacity: enterWash.opacity }}
          aria-hidden="true"
        />
      ) : null}

      <div className="mb-8 grid gap-4 md:grid-cols-[minmax(140px,1fr)_2fr_auto] md:items-start md:gap-8">
        <div>
          <h1 className={PROJECT_TOPIC_LABEL_CLASS}>{project.title.toLowerCase()}</h1>
          <p className="site-muted mt-2">{project.platformLabel}</p>
        </div>
        <p className="site-muted normal-case leading-relaxed">{project.shortDescription}</p>
        <p className="site-muted md:text-right">{project.year}</p>
      </div>

      {hasSections && previewVideo ? (
        <div className="mb-12" data-project-hero>
          <ProjectMedia
            title={project.title}
            video={previewVideo}
            poster={project.previewPoster}
            aspectClassName="aspect-video"
            playOnHover={false}
            autoPlay={isAutoPlay}
            playFromStart={fromExplore}
          />
          <ToggleSwitch
            id="case-study-autoplay"
            label="autoplay"
            checked={isAutoPlay}
            onChange={handleAutoPlayChange}
          />
        </div>
      ) : null}

      {hasSections ? (
        <ProjectCaseStudySections
          hero={project.hero}
          ipNote={project.ipNote}
          about={project.about}
          aboutTitle={project.aboutTitle}
          aboutMedia={project.aboutMedia}
          socialCards={project.socialCards}
          process={project.process}
          responsibilities={project.responsibilities}
          sections={project.sections}
          contentTitle={project.contentTitle}
        />
      ) : null}

      {!hasSections ? (
        <>
          <div className="mb-12" data-project-hero>
            <ProjectMedia
              title={project.title}
              video={projectVideo}
              poster={project.previewPoster}
              aspectClassName="aspect-video"
              playOnHover={false}
              autoPlay={isAutoPlay}
              playFromStart={fromExplore}
            />
            {projectVideo ? (
              <ToggleSwitch
                id="case-study-autoplay"
                label="autoplay"
                checked={isAutoPlay}
                onChange={handleAutoPlayChange}
              />
            ) : null}
          </div>

          {project.contentMedia?.length || project.contentAbout?.length ? (
            <section className="mb-12 w-full space-y-6">
              <h2 className={PROJECT_TOPIC_LABEL_CLASS}>
                {(project.contentSectionTitle || 'Content').toLowerCase()}
              </h2>
              {project.contentAbout?.map((paragraph) => (
                <p
                  key={paragraph.slice(0, 32)}
                  className="site-muted w-full normal-case leading-relaxed"
                >
                  {paragraph}
                </p>
              ))}
              {project.contentMedia?.length ? (
                <CaseStudyMediaCarousel
                  items={enrichCaseStudyCarouselItems(
                    project.contentMedia,
                    project.contentAboutMedia || []
                  )}
                  showSocialStats={Boolean(project.contentShowSocialStats)}
                  heading={project.contentTitle}
                />
              ) : null}
            </section>
          ) : null}

          <div className="w-full space-y-10">
          <section className="w-full space-y-5">
            <h2 className={PROJECT_TOPIC_LABEL_CLASS}>Role</h2>
            {project.roleBody?.length ? (
              project.roleBody.map((paragraph) => (
                <p
                  key={paragraph.slice(0, 32)}
                  className="site-muted w-full normal-case leading-relaxed"
                >
                  {paragraph}
                </p>
              ))
            ) : null}
            {project.responsibilities?.length ? (
              <ul className="w-full space-y-2">
                {project.responsibilities.map((item) => (
                  <li key={item} className="site-muted normal-case leading-relaxed">
                    — {item}
                  </li>
                ))}
              </ul>
            ) : null}
          </section>

          <section className="w-full space-y-5 border-t border-border pt-8">
            <h2 className={PROJECT_TOPIC_LABEL_CLASS}>
              {(project.breakdownSectionTitle || 'Technical Breakdown').toLowerCase()}
            </h2>
            {project.tools?.length > 0 ? (
              <ul className="space-y-2">
                {project.tools.map((item) => (
                  <li key={item} className="site-muted normal-case leading-relaxed">
                    — {item}
                  </li>
                ))}
              </ul>
            ) : null}
            {project.breakdownTitle || project.breakdownIntro || project.breakdown?.length ? (
              <div className="space-y-5">
                {project.breakdownTitle ? (
                  <h3 className={`${PROJECT_TOPIC_LABEL_CLASS} text-[11px]`}>{project.breakdownTitle.toLowerCase()}</h3>
                ) : null}
                {project.breakdownIntro ? (
                  <p className="site-muted normal-case leading-relaxed">{project.breakdownIntro}</p>
                ) : null}
                {project.breakdown?.length > 0 ? (
                  <ul className="space-y-2">
                    {project.breakdown.map((item) => (
                      <li key={item} className="site-muted normal-case leading-relaxed">
                        — {item}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
            {project.breakdownMedia ? (
              <div className="pt-3">
                <CaseStudyMedia
                  item={project.breakdownMedia}
                  title={project.breakdownMedia.label || project.title}
                />
              </div>
            ) : null}
          </section>

          {project.detailSections?.map((detailSection) => (
            <section
              key={detailSection.id || detailSection.title}
              className="space-y-5 border-t border-border pt-8"
            >
              <h2 className={PROJECT_TOPIC_LABEL_CLASS}>{detailSection.title.toLowerCase()}</h2>
              {detailSection.body?.map((paragraph) => (
                <p
                  key={paragraph.slice(0, 32)}
                  className="site-muted normal-case leading-relaxed"
                >
                  {paragraph}
                </p>
              ))}
              {detailSection.bullets?.length > 0 ? (
                <ul className="space-y-2">
                  {detailSection.bullets.map((item) => (
                    <li key={item} className="site-muted normal-case leading-relaxed">
                      — {item}
                    </li>
                  ))}
                </ul>
              ) : null}
              {renderDetailSectionMedia(
                detailSection.media,
                detailSection.mediaLayout,
                detailSection.title
              )}
            </section>
          ))}
          </div>
        </>
      ) : null}

      <nav
        className="mt-16 flex flex-col gap-4 border-t border-border pt-8 sm:flex-row sm:justify-between"
        aria-label="Project navigation"
      >
        {previous ? (
          <Link
            to={getProjectDetailsPath(previous.id)}
            className="site-muted hover:text-text-primary"
          >
            ← {previous.title.toLowerCase()}
          </Link>
        ) : (
          <span />
        )}
        {next ? (
          <Link
            to={getProjectDetailsPath(next.id)}
            className="site-muted hover:text-text-primary sm:text-right"
          >
            {next.title.toLowerCase()} →
          </Link>
        ) : (
          <span />
        )}
      </nav>
    </section>
  )
}

export default ProjectDetail
